import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import {
  setHullConfiguration,
  setSpecializedHull,
  setAdditionalHull,
  clearBreakawayState,
} from "../../../../store/features/shipBuilderSlice";
import { resetBreakawayHull } from "../../../../store/features/breakawayHullSlice";
import { setBreakawayHull as setPowerBreakawayHull } from "../../../../domains/power/powerSlice";
import { setBreakawayHull as setWeaponsBreakawayHull } from "../../../../domains/weapons/weaponsSlice";
import { setBreakawayHull as setDrivesBreakawayHull } from "../../../../domains/drives/drivesSlice";
import { setBreakawayHull as setCrewBreakawayHull } from "../../../../domains/crew/crewSlice";


import {
  HULL_CONFIGURATIONS,
  HULL_OPTIONS,
  SPECIALIZED_HULLS,
  ADDITIONAL_HULLS,
} from "../../../../constants/hull";
import { ARMOR_TYPES } from "../../../../constants/armor";
import type { Hull } from "../../../../types/ship";
import { CardSelector } from "./CardSelector";
import { BreakawayConfigurationModal } from "../breakaway/BreakawayConfigurationModal";

/**
 * HullSelector Component
 *
 * Allows users to select and configure the ship's hull, including:
 * - Basic hull size and configuration
 * - Specialized hull types
 * - Additional hull features
 * - Armor options
 *
 * The component handles all hull-related calculations and validations.
 */
const hullConfigurationsArray = [...HULL_CONFIGURATIONS];
const hullOptionsArray = Object.values(HULL_OPTIONS);
const specializedHullTypesArray = Object.values(SPECIALIZED_HULLS);
const additionalHullTypesArray = Object.values(ADDITIONAL_HULLS);

// Validation helpers for Solar Coating
const allowedSolarCoatingHullTypes = [
  "standard",
  "sphere",
  "closeStructure",
  "dispersedStructure",
];
const disallowedSolarCoatingHullTypes = [
  "streamlined",
  "planetoid",
  "bufferedPlanetoid",
];

const isSolarCoatingPercentValid = (percent: number) =>
  percent >= 0 && percent <= 40;
const isSolarCoatingHullTypeAllowed = (hullType?: string) =>
  hullType && allowedSolarCoatingHullTypes.includes(hullType);

export const HullSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const hull = useAppSelector(selectors.selectHull);
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const shipSize = useAppSelector(selectors.selectShipSize);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const [isBreakawayEnabled, setIsBreakawayEnabled] = useState(false);
  const [showBreakawayModal, setShowBreakawayModal] = useState(false);
  const [armorProtectionInput, setArmorProtectionInput] = useState<string>((hull?.armor?.rating || 0).toString());
  const [userHasManuallySelectedArmor, setUserHasManuallySelectedArmor] = useState(false);

  // Handler for selecting armor and protection
  const handleArmorSelect = (
    armorType: keyof typeof ARMOR_TYPES | "",
    protection?: number,
    isUserSelection: boolean = false,
  ) => {
    if (!hull) return;

    // Mark that user has made a manual selection if this is a user action
    if (isUserSelection) {
      setUserHasManuallySelectedArmor(true);
    }

    if (armorType === "") {
      // Set armor to a special "None" object to indicate it was explicitly selected
      dispatch(setHullConfiguration({ 
        ...hull, 
        armor: {
          type: "",
          rating: 0,
          costPerTon: 0,
          weightPerTon: 0,
          description: "None"
        }
      }));
      return;
    }

    const armorConfig = ARMOR_TYPES[armorType];
    
    // Calculate maximum protection based on tech level
    const maxProtection = armorConfig.maxProtection(techLevel);
    
    // If protection is provided, clamp it to valid range; otherwise default to 0
    const selectedProtection = protection !== undefined ? 
      Math.max(0, Math.min(protection, maxProtection)) : 0;

    const armor = {
      type: armorType,
      rating: selectedProtection,
      costPerTon: armorConfig.costPerTon,
      weightPerTon: armorConfig.tonnageConsumed,
      description: armorConfig.name,
    };

    dispatch(setHullConfiguration({ ...hull, armor }));
  };

  // Auto-select the best available armor when tech level changes or hull is first created
  React.useEffect(() => {
    if (!hull || isBreakawayEnabled) return;

    // Find the highest-rated armor available for current tech level
    const availableArmor = Object.entries(ARMOR_TYPES)
      .filter(([_, armorSpec]) => tlOverrideActive || armorSpec.techLevel <= techLevel)
      .sort(([_, a], [__, b]) => b.techLevel - a.techLevel); // Sort by tech level descending

    if (availableArmor.length > 0) {
      const [bestArmorType] = availableArmor[0];
      const bestArmorSpec = ARMOR_TYPES[bestArmorType as keyof typeof ARMOR_TYPES];
      
      // Auto-select if:
      // 1. No armor is configured at all (undefined), OR
      // 2. Current armor is invalid at new tech level (too high tech level), OR
      // 3. User hasn't made a manual selection yet AND there's better armor available
      const shouldAutoSelect = 
        hull.armor === undefined || // No armor selected
        (hull.armor.type === "" && hull.armor.description === "None") || // Explicitly selected "None"
        (hull.armor.type && ARMOR_TYPES[hull.armor.type as keyof typeof ARMOR_TYPES]?.techLevel > techLevel) || // Current armor invalid (too high TL)
        (!userHasManuallySelectedArmor && hull.armor.type && ARMOR_TYPES[hull.armor.type as keyof typeof ARMOR_TYPES]?.techLevel < bestArmorSpec.techLevel); // Better armor available but only if user hasn't manually selected

      if (shouldAutoSelect) {
        handleArmorSelect(bestArmorType as keyof typeof ARMOR_TYPES, 0);
      }
    }
  }, [techLevel, hull, isBreakawayEnabled, tlOverrideActive, userHasManuallySelectedArmor]);

  // Sync local armor protection input state with Redux state
  React.useEffect(() => {
    setArmorProtectionInput((hull?.armor?.rating || 0).toString());
  }, [hull?.armor?.type, hull?.armor?.rating]);

  // Handler for armor protection input changes
  const handleArmorProtectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    
    // Allow empty input or any numeric input while typing
    if (inputValue === '' || /^\d*$/.test(inputValue)) {
      setArmorProtectionInput(inputValue);
    }
  };

  const handleArmorProtectionBlur = (armorType: keyof typeof ARMOR_TYPES) => {
    const protection = parseInt(armorProtectionInput, 10);
    
    if (!isNaN(protection) && hull?.armor?.type === armorType) {
      const armorConfig = ARMOR_TYPES[armorType];
      const maxProtection = armorConfig.maxProtection(techLevel);
      
      // Clamp to valid range
      const clampedProtection = Math.max(0, Math.min(protection, maxProtection));
      
      // Update local state with clamped value
      setArmorProtectionInput(clampedProtection.toString());
      
      // Update Redux state (don't mark as user selection since this is just protection level change)
      handleArmorSelect(armorType, clampedProtection, false);
    } else {
      // Invalid input, reset to current value
      setArmorProtectionInput((hull?.armor?.rating || 0).toString());
    }
  };

  const handleConfigurationChange = (configKey: string) => {
    const configuration = hullConfigurationsArray.find(
      (c) => c.type === configKey,
    );
    if (!hull || !configuration) return;

    const updatedHull: Partial<Hull> = {
      ...hull,
      configuration: {
        type: configuration.type,
        name: configuration.name,
        description: configuration.description,
        costModifier: configuration.costModifier,
        costPerTon: configuration.costPerTon,
        weightModifier: configuration.weightModifier,
        hullPointsPerTon: configuration.hullPointsPerTon,
        ...("baseCost" in configuration && {
          baseCost: configuration.baseCost,
        }),
        ...("usableVolume" in configuration && {
          usableVolume: configuration.usableVolume,
        }),
        examples: configuration.examples,
        streamlined: configuration.streamlined,
        armorVolumeModifier: configuration.armorVolumeModifier,
      },
      options: hull.options || [],
    };

    dispatch(setHullConfiguration(updatedHull));
  };

  // Helper for mutual exclusion and tech level
  const getOptionDisabled = (key: string) => {
    if (!hull) return true;
    // Tech level enforcement
    const option = hullOptionsArray.find((o) => o.type === key);
    if (!option) return true;
    if (techLevel < option.techLevel) return true;

    const selected = (hull.options || []).map((o) => o.type);
    // Mutual exclusion rules
    if (key === "reflec" && selected.includes("stealth")) return true;
    if (key === "stealth" && selected.includes("reflec")) return true;
    if (
      key === "solarCoating" &&
      selected.some((t) => t !== "solarCoating" && t !== "radiationShielding")
    )
      return true;
    if (
      key !== "solarCoating" &&
      key !== "radiationShielding" &&
      selected.includes("solarCoating")
    )
      return true;
    return false;
  };

  const handleOptionChange = (optionKey: string) => {
    const option = hullOptionsArray.find((o) => o.type === optionKey);
    if (!hull || !option) return;

    let options = [...(hull.options || [])];
    const existingIndex = options.findIndex((o) => o.type === option.type);

    if (existingIndex !== -1) {
      options.splice(existingIndex, 1);
    } else {
      // Deselect incompatible options automatically
      if (option.type === "reflec") {
        options = options.filter((o) => o.type !== "stealth");
      }
      if (option.type === "stealth") {
        options = options.filter((o) => o.type !== "reflec");
      }
      if (option.type === "solarCoating") {
        options = options.filter((o) => o.type === "radiationShielding");
      }
      if (
        option.type !== "solarCoating" &&
        option.type !== "radiationShielding"
      ) {
        options = options.filter((o) => o.type !== "solarCoating");
      }
      options.push({
        type: option.type,
        name: option.name,
        costPerTon: option.costPerTon,
        techLevel: option.techLevel,
        requirements: option.requirements ? [...option.requirements] : [],
      });
    }

    const updatedHull: Partial<Hull> = {
      ...hull,
      options,
    };

    dispatch(setHullConfiguration(updatedHull));
  };

  const handleSpecializedTypeChange = (specializedTypeKey?: string) => {
    if (!hull) return;

    const specializedType = specializedHullTypesArray.find(
      (t) => t.type === specializedTypeKey,
    );

    const updatedHull: Partial<Hull> = {
      ...hull,
      specializedType: specializedType
        ? {
            type: specializedType.type,
            name: specializedType.name,
            description: specializedType.description,
            costModifier: specializedType.costModifier,
            ...("hullPointsModifier" in specializedType && {
              hullPointsModifier: specializedType.hullPointsModifier,
            }),
            ...("armorMultiplier" in specializedType && {
              armorMultiplier: specializedType.armorMultiplier,
            }),
            ...("powerModifier" in specializedType && {
              powerModifier: specializedType.powerModifier,
            }),
            ...("maxSize" in specializedType && {
              maxSize: specializedType.maxSize,
            }),
            requirements: specializedType.requirements
              ? [...specializedType.requirements]
              : undefined,
          }
        : undefined,
    };

    dispatch(setSpecializedHull(updatedHull));
  };

  const handleAdditionalTypeChange = (typeKey: string) => {
    if (!hull) return;

    // If breakaway hulls are selected, open the configuration modal
    if (typeKey === "breakaway") {
      setShowBreakawayModal(true);
      setIsBreakawayEnabled(true);
      return;
    }

    // If we're deselecting breakaway hulls, clear all breakaway-related state
    if (hull.additionalType?.type === "breakaway" && typeKey !== "breakaway") {
      // Clear breakaway hull state
      dispatch(resetBreakawayHull());
      
      // Clear breakaway-related state from all slices
      dispatch(setPowerBreakawayHull(false));
      dispatch(setWeaponsBreakawayHull(false));
      dispatch(setDrivesBreakawayHull(false));
      dispatch(setCrewBreakawayHull({ isBreakaway: false, sections: [] }));

      
      // Clear drives array and other breakaway state
      dispatch(clearBreakawayState());
      
      console.log('Cleared all breakaway hull state');
    }

    const additionalType = typeKey
      ? additionalHullTypesArray.find((t) => t.type === typeKey)
      : undefined;

    const updatedHull: Partial<Hull> = {
      ...hull,
      additionalType: additionalType
        ? {
            type: additionalType.type,
            name: additionalType.name,
            description: additionalType.description,
            costModifier: additionalType.costModifier ?? 1,
            ...("costPerTon" in additionalType && {
              costPerTon: additionalType.costPerTon,
            }),
            ...("minOuterHullSize" in additionalType && {
              minOuterHullSize: additionalType.minOuterHullSize,
            }),
            ...("minRingRadius" in additionalType && {
              minRingRadius: additionalType.minRingRadius,
            }),
            ...("spinMachineryPerTon" in additionalType && {
              spinMachineryPerTon: additionalType.spinMachineryPerTon,
            }),
            ...("tonnageConsumed" in additionalType && {
              tonnageConsumed: additionalType.tonnageConsumed,
            }),
            ...("separationTime" in additionalType && {
              separationTime: additionalType.separationTime,
            }),
            requirements: additionalType.requirements
              ? [...additionalType.requirements]
              : [],
            percent: 0,
          }
        : undefined,
    };

    dispatch(setAdditionalHull(updatedHull));
  };

  // Handler to update solarCoating percent
  const handleSolarCoatingPercentChange = (percent: number) => {
    if (!hull) return;
    let options = [...(hull.options || [])];
    const idx = options.findIndex((o) => o.type === "solarCoating");
    if (idx !== -1) {
      options[idx] = {
        ...options[idx],
        percent: Math.max(0, Math.min(40, percent)),
      };
      dispatch(setHullConfiguration({ ...hull, options }));
    }
  };

  // Handler to update additional hull type percent
  const handleAdditionalTypePercentChange = (percent: number) => {
    if (!hull || !hull.additionalType) return;

    const clampedPercent = Math.max(0, Math.min(100, percent));

    const updatedHull: Partial<Hull> = {
      ...hull,
      additionalType: {
        ...hull.additionalType,
        percent: clampedPercent,
      },
    };
    dispatch(setAdditionalHull(updatedHull));
  };

  const handleStealthTierChange = (tier: string) => {
    if (!hull) return;

    const options = (hull.options || []).map((o) => {
      if (o.type === "stealth") {
        return { ...o, tier };
      }
      return o;
    });

    dispatch(setHullConfiguration({ ...hull, options }));
  };

  // Helper for getExtra in CardSelector
  const getOptionExtra = (key: string) => {
    if (
      key === "solarCoating" &&
      hull?.options?.some((o) => o.type === "solarCoating")
    ) {
      const option = hull.options.find((o) => o.type === "solarCoating");
      const percent =
        option && typeof option.percent === "number" ? option.percent : 0;
      const hullType = hull?.configuration?.type;
      const percentValid = isSolarCoatingPercentValid(percent);
      const hullTypeAllowed = isSolarCoatingHullTypeAllowed(hullType);
      let errorMsg = "";
      if (!hullTypeAllowed) {
        errorMsg =
          hullType && disallowedSolarCoatingHullTypes.includes(hullType)
            ? `Solar Coating cannot be installed on hull type: ${hullType}`
            : `Solar Coating is not allowed on hull type: ${hullType || "(none)"}`;
      } else if (!percentValid) {
        errorMsg = "Solar Coating cannot cover more than 40% of the hull.";
      }
      return (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-700">
            % of Hull Coated (max 40%)
          </label>
          <input
            type="number"
            min={0}
            max={40}
            value={percent}
            onChange={(e) =>
              handleSolarCoatingPercentChange(Number(e.target.value))
            }
            onClick={(e) => e.stopPropagation()}
            className={`mt-1 block w-24 rounded-md border shadow-sm sm:text-xs focus:border-indigo-500 focus:ring-indigo-500 ${
              !percentValid || !hullTypeAllowed
                ? "border-red-500"
                : "border-gray-300"
            }`}
            disabled={!hullTypeAllowed}
          />
          {errorMsg && <p className="mt-1 text-xs text-red-600">{errorMsg}</p>}
        </div>
      );
    }

    if (key === "stealth" && hull?.options?.some((o) => o.type === "stealth")) {
      const stealthOption = HULL_OPTIONS.stealth;
      const selectedTier = hull.options.find((o) => o.type === "stealth")?.tier;

      return (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            Stealth Tier
          </label>
          <select
            value={selectedTier || "basic"}
            onChange={(e) => handleStealthTierChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs dark:bg-gray-700 dark:text-gray-100"
          >
            {(stealthOption.types || []).map((tier) => (
              <option key={tier.type} value={tier.type}>
                {tier.type.charAt(0).toUpperCase() + tier.type.slice(1)} (TL
                {tier.techLevel})
              </option>
            ))}
          </select>
        </div>
      );
    }

    return null;
  };

  // Helper for getExtra in CardSelector for Additional Types
  const getAdditionalTypeExtra = (key: string) => {
    if (!hull?.additionalType || hull.additionalType.type !== key) return null;

    const additionalType = hull.additionalType;
    const percent = additionalType.percent || 0;

    if (key === "doubleHull") {
      return (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            % of Hull Spun (max 100%)
          </label>
          <input
            type="number"
            min={0}
            max={100}
            value={percent}
            onChange={(e) =>
              handleAdditionalTypePercentChange(Number(e.target.value))
            }
            onClick={(e) => e.stopPropagation()}
            className="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs dark:bg-gray-700 dark:text-gray-100"
          />
        </div>
      );
    }

    if (key === "hamsterCage") {
      return (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            % of Hull in Rings (max 100%)
          </label>
          <input
            type="number"
            min={0}
            max={100}
            value={percent}
            onChange={(e) =>
              handleAdditionalTypePercentChange(Number(e.target.value))
            }
            onClick={(e) => e.stopPropagation()}
            className="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs dark:bg-gray-700 dark:text-gray-100"
          />
        </div>
      );
    }

    if (
      key === "solarCoating" &&
      hull?.options?.some((o) => o.type === "solarCoating")
    ) {
      return (
        <div className="mt-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
            % of Hull Coated (max 40%)
          </label>
          <input
            type="number"
            min={0}
            max={40}
            value={percent}
            onChange={(e) =>
              handleSolarCoatingPercentChange(Number(e.target.value))
            }
            onClick={(e) => e.stopPropagation()}
            className="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs dark:bg-gray-700 dark:text-gray-100"
          />
        </div>
      );
    }

    if (key === "breakaway") {
      return (
        <div className="mt-2">
          <div className="text-xs text-gray-600">
            Breakaway hulls are configured inline below
          </div>
        </div>
      );
    }

    return null;
  };

  const armorItems = React.useMemo(
    () =>
      Object.entries(ARMOR_TYPES)
        .filter(([_, value]) => tlOverrideActive || value.techLevel <= techLevel) // Filter by tech level
        .map(([key, value]) => ({
          key,
          title: value.name,
          maxProtection: value.maxProtection(techLevel),
          extra: (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                Min TL {value.techLevel}
              </span>
            </div>
          ),
        })),
    [techLevel, tlOverrideActive],
  );

  if (!hull) return null;

  const getArmorExtra = (key: string) => {
    if (key !== hull?.armor?.type) return null;

    const armorItem = armorItems.find((item) => item.key === key);
    if (!armorItem) return null;

    return (
      <div className="mt-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Protection Level (max {armorItem.maxProtection})
        </label>
        <input
          type="number"
          min="0"
          max={armorItem.maxProtection}
          value={armorProtectionInput}
          onChange={handleArmorProtectionChange}
          onBlur={() => handleArmorProtectionBlur(key as keyof typeof ARMOR_TYPES)}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Hull Configuration
        </h2>
      </div>
      <div className="flex flex-col space-y-8">
        <div>
          <CardSelector
            label="Hull Configuration"
            items={hullConfigurationsArray.map((config) => ({
              key: config.type,
              title: config.name,
            }))}
            selected={hull?.configuration?.type || ""}
            onSelect={handleConfigurationChange}
          />

          <CardSelector
            label="Specialized Hull Type"
            items={[
              {
                key: "",
                title: "None",
              },
              ...specializedHullTypesArray.map((specialized) => ({
                key: specialized.type,
                title: specialized.name,
              })),
            ]}
            selected={hull?.specializedType?.type || ""}
            onSelect={handleSpecializedTypeChange}
            className="mt-8"
          />

          <CardSelector
            label="Additional Hull Type"
            items={[
              {
                key: "",
                title: "None",
              },
              ...additionalHullTypesArray.map((item) => ({
                key: item.type,
                title: item.name,
              })),
            ]}
            selected={hull?.additionalType?.type || ""}
            onSelect={handleAdditionalTypeChange}
            getExtra={getAdditionalTypeExtra}
            className="mt-8"
          />
        </div>
        <div>
          <CardSelector
            label="Hull Options"
            items={hullOptionsArray.map((item) => ({
              key: item.type,
              title: item.name,
              extra: (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    Min TL {item.techLevel}
                  </span>
                </div>
              ),
            }))}
            selected={(hull?.options || []).map((o) => o.type)}
            onSelect={handleOptionChange}
            multiSelect
            getDisabled={getOptionDisabled}
            getExtra={getOptionExtra}
          />

          {!isBreakawayEnabled ? (
            <CardSelector
              label="Armor"
              items={[
                {
                  key: "",
                  title: "None",
                },
                ...armorItems,
              ]}
              selected={hull?.armor?.type || ""}
              onSelect={(type) =>
                handleArmorSelect(type as keyof typeof ARMOR_TYPES, undefined, true)
              }
              getExtra={getArmorExtra}
              className="mt-8"
            />
          ) : (
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Armor Configuration</h3>
              <p className="text-sm text-blue-800">
                Armor is configured per section for breakaway hulls. Each section can have different armor types 
                and protection levels. Configure armor for each section in the breakaway hull configuration below.
              </p>
            </div>
          )}
        </div>
      </div>
      


      {/* Breakaway Configuration Modal */}
      <BreakawayConfigurationModal
        isOpen={showBreakawayModal}
        onClose={() => setShowBreakawayModal(false)}
        shipSize={shipSize}
        onConfigChange={setIsBreakawayEnabled}
      />
    </div>
  );
};
